import React, { useState, useEffect } from 'react';

interface RBACOnboardingGuardProps {
  errorCode: 'tenant_user_missing' | null;
  onRetry?: () => void;
}

const MAX_RETRIES = 3;
const AUTO_RETRY_DELAY = 3000;

export function RBACOnboardingGuard({ errorCode, onRetry }: RBACOnboardingGuardProps) {
  const [retryCount, setRetryCount] = useState(0);
  const [autoRetrying, setAutoRetrying] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_RETRY_DELAY / 1000);

  useEffect(() => {
    if (errorCode === 'tenant_user_missing' && retryCount < MAX_RETRIES) {
      setAutoRetrying(true);
      setCountdown(AUTO_RETRY_DELAY / 1000);

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      const retryTimeout = setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        setAutoRetrying(false);
        if (onRetry) {
          onRetry();
        } else {
          window.location.reload();
        }
      }, AUTO_RETRY_DELAY);

      return () => {
        clearTimeout(retryTimeout);
        clearInterval(countdownInterval);
      };
    }
  }, [errorCode, retryCount, onRetry]);

  if (errorCode !== 'tenant_user_missing') {
    return null;
  }

  const handleManualRetry = () => {
    setRetryCount((prev) => prev + 1);
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const maxRetriesReached = retryCount >= MAX_RETRIES;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          maxWidth: 500,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              margin: '0 auto 16px',
              background: '#FFF3E0',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
            }}
          >
            ⚙️
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f2b5a', margin: '0 0 12px 0' }}>
            Account wird eingerichtet
          </h2>
          <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
            Du bist eingeloggt, aber dein Account ist diesem Tenant noch nicht zugeordnet.
          </p>
        </div>

        <div
          style={{
            padding: 16,
            background: '#E3F2FD',
            border: '1px solid #2196F3',
            borderRadius: 8,
            marginBottom: 24,
            fontSize: 13,
            color: '#0D47A1',
          }}
        >
          <strong>ℹ️ Was passiert jetzt?</strong>
          <br />
          Das System richtet deinen Zugang automatisch ein. Dies dauert nur wenige Sekunden.
        </div>

        {!maxRetriesReached && (
          <div style={{ marginBottom: 24 }}>
            {autoRetrying && (
              <p style={{ fontSize: 14, color: '#666', textAlign: 'center', margin: '0 0 16px 0' }}>
                Automatischer Neuversuch in {countdown} Sekunden...
              </p>
            )}
            <button
              onClick={handleManualRetry}
              disabled={autoRetrying}
              style={{
                width: '100%',
                padding: '12px 24px',
                background: autoRetrying ? '#ccc' : '#021a4a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: autoRetrying ? 'not-allowed' : 'pointer',
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              {autoRetrying ? 'Wird neu geladen...' : 'Jetzt neu laden'}
            </button>
            {retryCount > 0 && (
              <p style={{ fontSize: 12, color: '#999', textAlign: 'center', margin: '8px 0 0 0' }}>
                Versuch {retryCount} von {MAX_RETRIES}
              </p>
            )}
          </div>
        )}

        {maxRetriesReached && (
          <div
            style={{
              padding: 16,
              background: '#FFEBEE',
              border: '1px solid #F44336',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
              color: '#C62828',
            }}
          >
            <strong>⚠️ Maximale Versuche erreicht</strong>
            <br />
            Bitte kontaktiere den Support. Gib dabei die folgende Trace-ID an:
            <br />
            <code
              style={{
                display: 'block',
                marginTop: 8,
                padding: 8,
                background: '#fff',
                borderRadius: 4,
                fontSize: 12,
                fontFamily: 'monospace',
              }}
            >
              {/* TODO: Get actual X-Trace-Id from error response */}
              Trace-ID: {Date.now()}
            </code>
          </div>
        )}
      </div>
    </div>
  );
}
